import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore'
import { auth, db } from '../firebase.config'

interface User {
  uid: string
  email: string | null
  displayName: string | null
}

interface UserData {
  uid: string
  name: string
  email: string
  role: 'admin' | 'supervisor' | 'foreman' | 'superintendant' | 'member'
  status: 'approved' | 'pending' | 'rejected'
  orgId: string
  assignedProjectId?: string
  plan?: string
  crewId?: string
  reportsTo?: string
}

interface OrgData {
  id: string
  name: string
  plan: string
  workAreas?: string[]
  categories?: string[]
}

interface Project {
  id: string
  name: string
  code?: string
  orgId: string
  status?: string
  location?: { latitude: number; longitude: number; address?: string }
  musterRadiusMeters?: number
}

interface AuthContextType {
  currentUser: User | null
  userData: UserData | null
  orgData: OrgData | null
  orgId: string
  projects: Project[]
  selectedProjectId: string
  selectedProject: Project | null
  loading: boolean
  isProjectView: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setSelectedProjectId: (id: string) => void
  isAdmin: () => boolean
  isSupervisor: () => boolean
  isForemanOrAbove: () => boolean
  hasFeature: (feature: string) => boolean
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

const PLAN_ORDER = ['core', 'field', 'premium', 'enterprise']
const FEATURE_PLANS: Record<string, string[]> = {
  jha: ['field', 'premium', 'enterprise'],
  safety: ['field', 'premium', 'enterprise'],
  toolbox: ['field', 'premium', 'enterprise'],
  reports: ['premium', 'enterprise'],
  timesheets: ['field', 'premium', 'enterprise'],
  certifications: ['premium', 'enterprise'],
  equipment: ['premium', 'enterprise'],
  communications: ['premium', 'enterprise'],
  users: ['core', 'field', 'premium', 'enterprise'],
  settings: ['admin'],
  crewmap: ['enterprise'],
  tasks: ['premium', 'enterprise'],
}

export const PLAN_LIMITS: Record<string, number> = {
  core: 4,
  field: 15,
  premium: 50,
  enterprise: 150,
}

const STORAGE_KEY = (orgId: string) => `sitedeck_project_${orgId}`

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [orgData, setOrgData] = useState<OrgData | null>(null)
  const [orgId, setOrgId] = useState<string>('')
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectIdState] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mountedRef.current) return

      if (!user) {
        setCurrentUser(null)
        setUserData(null)
        setOrgData(null)
        setOrgId('')
        setProjects([])
        setSelectedProjectIdState('')
        setLoading(false)
        return
      }

      setCurrentUser({ uid: user.uid, email: user.email, displayName: user.displayName })

      const uid = user.uid

      // Step 1: /userOrgs/{uid} lookup doc
      try {
        const snap = await getDoc(doc(db, 'userOrgs', uid))
        if (snap.exists()) {
          const resolvedOrgId = snap.data().orgId as string | undefined
          if (resolvedOrgId) {
            try {
              const [userDoc, orgDoc] = await Promise.all([
                getDoc(doc(db, 'organizations', resolvedOrgId, 'users', uid)),
                getDoc(doc(db, 'organizations', resolvedOrgId))
              ])
              if (!mountedRef.current) return
              if (userDoc.exists()) {
                setUserData({ uid: userDoc.id, ...userDoc.data() } as UserData)
              }
              if (orgDoc.exists()) {
                setOrgData({ id: orgDoc.id, ...orgDoc.data() } as OrgData)
              }
              setOrgId(resolvedOrgId)
              initProjectSelection(resolvedOrgId, { uid: userDoc.id, ...userDoc.data() } as UserData)
            } catch (e) {
              if (!mountedRef.current) return
              console.error('AuthContext: error loading org data', e)
            }
            setLoading(false)
            return
          }
        }
      } catch (e) {
        if (!mountedRef.current) return
        console.error('AuthContext: /userOrgs lookup failed', e)
      }

      // Step 2: flat /users/{uid} (legacy)
      try {
        const snap = await getDoc(doc(db, 'users', uid))
        if (snap.exists()) {
          const resolvedOrgId = snap.data().orgId as string | undefined
          if (resolvedOrgId) {
            try {
              const [userDoc, orgDoc] = await Promise.all([
                getDoc(doc(db, 'organizations', resolvedOrgId, 'users', uid)),
                getDoc(doc(db, 'organizations', resolvedOrgId))
              ])
              if (!mountedRef.current) return
              if (userDoc.exists()) {
                setUserData({ uid: userDoc.id, ...userDoc.data() } as UserData)
              }
              if (orgDoc.exists()) {
                setOrgData({ id: orgDoc.id, ...orgDoc.data() } as OrgData)
              }
              setOrgId(resolvedOrgId)
              initProjectSelection(resolvedOrgId, { uid: userDoc.id, ...userDoc.data() } as UserData)
            } catch (e) {
              if (!mountedRef.current) return
              console.error('AuthContext: error loading org data (legacy)', e)
            }
            setLoading(false)
            return
          }
        }
      } catch (e) {
        if (!mountedRef.current) return
        console.error('AuthContext: flat /users lookup failed', e)
      }

      if (!mountedRef.current) return
      console.error('AuthContext: could not resolve orgId for uid', uid)
      setLoading(false)
    })

    return () => {
      mountedRef.current = false
      unsubscribe()
    }
  }, [])

  // Load projects for the current org
  useEffect(() => {
    if (!orgId) {
      setProjects([])
      return
    }
    const q = query(collection(db, 'projects'), where('orgId', '==', orgId))
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as Project))
      setProjects(list)
    })
    return () => unsub()
  }, [orgId])

  const initProjectSelection = (resolvedOrgId: string, uData: UserData) => {
    const isAdminUser = uData.role === 'admin'
    if (!isAdminUser) {
      // Non-admin users are locked to their assigned project
      setSelectedProjectIdState(uData.assignedProjectId || '')
      return
    }
    // Admin: restore last selection or default to org view
    try {
      const saved = localStorage.getItem(STORAGE_KEY(resolvedOrgId))
      setSelectedProjectIdState(saved || '')
    } catch (e) {
      setSelectedProjectIdState('')
    }
  }

  const setSelectedProjectId = (id: string) => {
    setSelectedProjectIdState(id)
    if (orgId) {
      try {
        if (id) {
          localStorage.setItem(STORAGE_KEY(orgId), id)
        } else {
          localStorage.removeItem(STORAGE_KEY(orgId))
        }
      } catch (e) {
        console.error('AuthContext: failed to persist project selection', e)
      }
    }
  }

  const selectedProject = projects.find(p => p.id === selectedProjectId) || null
  const isAdmin = () => userData?.role === 'admin'
  const isSupervisor = () => ['admin', 'supervisor', 'superintendant'].includes(userData?.role || '')
  const isForemanOrAbove = () => ['admin', 'supervisor', 'superintendant', 'foreman'].includes(userData?.role || '')
  const isProjectView = selectedProjectId !== ''

  const hasFeature = (feature: string) => {
    const orgPlan = orgData?.plan || 'core'
    const requiredPlans = FEATURE_PLANS[feature]
    if (!requiredPlans) return true
    const userPlanIdx = PLAN_ORDER.indexOf(orgPlan)
    return requiredPlans.some(p => PLAN_ORDER.indexOf(p) <= userPlanIdx)
  }

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password)
  }

  const logout = async () => {
    sessionStorage.removeItem('sitedeck_auth')
    await signOut(auth)
  }

  return (
    <AuthContext.Provider value={{
      currentUser,
      userData,
      orgData,
      orgId,
      projects,
      selectedProjectId,
      selectedProject,
      loading,
      isProjectView,
      login,
      logout,
      setSelectedProjectId,
      isAdmin,
      isSupervisor,
      isForemanOrAbove,
      hasFeature,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
